/* ==========================================================================
   Aba líder dos alertas de atendimento (change customer-agent-handoff, D19)

   Com o painel aberto em várias abas do mesmo navegador, só uma toca o som e
   mostra a notificação.

   A liderança é um lock exclusivo da Web Locks API
   (`navigator.locks`, nome `vaptmenu:conversation-alerts:{tenantId}`), que a
   aba segura enquanto vive: as outras esperam na fila, e o navegador solta o
   lock na hora em que a líder fecha ou trava, sem depender de timer. Isso
   importa porque o Chrome junta os timers de uma aba oculta há mais de
   5 minutos numa execução por minuto: com a eleição por heartbeat, as abas em
   segundo plano se cortavam umas às outras e todas viravam líder (o som tocava
   em dobro ou em triplo).

   O `BroadcastChannel` de mesmo nome fica para o resto:
   - cada aba anuncia `heartbeat { id, audioUnlocked, leader, at }` a cada
     4 s (ou quando o navegador deixa), e responde na hora ao heartbeat de uma
     aba que ainda não conhecia; uma aba sem sinal há 75 s sai da lista (mais
     de um minuto, para tolerar o throttling), e `pagehide` manda `bye`;
   - a lista serve para "alguma aba liberou o som?" e para a preferência: a
     aba com som liberado que vê a líder sem som toma o lock (`steal`), e a
     antiga volta para a fila;
   - o aviso de silêncio (`alert-mute.ts`).

   Sem a Web Locks API, a líder é eleita pela lista: entre as abas vivas, a de
   som liberado com o menor id, e sem nenhuma liberada, a de menor id; fechada
   a líder sem `bye`, outra assume quando ela sai da lista. Sem
   `BroadcastChannel` e sem locks, toda aba é líder: pode tocar em dobro, o que
   é melhor do que não tocar.
   ========================================================================== */

export const ALERT_HEARTBEAT_MS = 4_000;
/** Mais de um minuto: uma aba oculta há muito tempo só roda timer uma vez por minuto. */
export const ALERT_PEER_EXPIRY_MS = 75_000;

export function alertChannelName(tenantId: string): string {
  return `vaptmenu:conversation-alerts:${tenantId}`;
}

export type AlertChannelMessage =
  | { type: 'heartbeat'; id: string; audioUnlocked: boolean; leader?: boolean; at: number }
  | { type: 'bye'; id: string }
  | { type: 'mute'; until: number };

/** O mínimo de `BroadcastChannel` que as abas usam (os testes passam um falso). */
export interface AlertChannel {
  postMessage(message: AlertChannelMessage): void;
  addEventListener(type: 'message', listener: (event: MessageEvent<AlertChannelMessage>) => void): void;
  removeEventListener(type: 'message', listener: (event: MessageEvent<AlertChannelMessage>) => void): void;
  close(): void;
}

export type AlertChannelFactory = (name: string) => AlertChannel | null;

/** O mínimo do `LockManager` da Web Locks API que a aba líder usa (os testes passam um falso). */
export interface AlertLockManager {
  request(
    name: string,
    options: { mode?: 'exclusive'; steal?: boolean; signal?: AbortSignal },
    callback: (lock: unknown) => Promise<void>,
  ): Promise<unknown>;
}

/** O `BroadcastChannel` do navegador, ou nulo quando ele não existe ou recusa abrir. */
export const browserAlertChannel: AlertChannelFactory = (name) => {
  if (typeof BroadcastChannel === 'undefined') return null;
  try {
    return new BroadcastChannel(name) as unknown as AlertChannel;
  } catch {
    return null;
  }
};

/** O `navigator.locks` do navegador, ou nulo quando a API não existe. */
export function browserAlertLocks(): AlertLockManager | null {
  try {
    const locks = typeof navigator === 'undefined' ? undefined : (navigator as Navigator & { locks?: AlertLockManager }).locks;
    return locks && typeof locks.request === 'function' ? locks : null;
  } catch {
    return null;
  }
}

export interface AlertLeaderOptions {
  tenantId: string;
  /** O som desta aba já está liberado? (lido a cada heartbeat) */
  audioUnlocked: () => boolean;
  channelFactory?: AlertChannelFactory;
  /** A Web Locks API; `navigator.locks` por padrão, e nulo para eleger pela lista. */
  locks?: AlertLockManager | null;
  /** O id desta aba; aleatório por padrão. */
  tabId?: string;
  now?: () => number;
  /** Onde ouvir o `pagehide` (a janela, por padrão). */
  pageEvents?: Pick<Window, 'addEventListener' | 'removeEventListener'> | null;
}

export interface AlertLeaderSnapshot {
  /** Esta aba toca o som e notifica. */
  isLeader: boolean;
  /** Alguma aba viva (inclusive esta) já liberou o som. */
  anyAudioUnlocked: boolean;
  /** Há lock ou canal entre as abas; sem nenhum, toda aba é líder. */
  coordinated: boolean;
}

export interface AlertLeader {
  readonly tabId: string;
  snapshot(): AlertLeaderSnapshot;
  subscribe(listener: (snapshot: AlertLeaderSnapshot) => void): () => void;
  /** O som desta aba foi liberado agora: anuncia já, sem esperar o ciclo. */
  announce(): void;
  /** Recebe os avisos de silêncio das outras abas. */
  onMute(listener: (until: number) => void): () => void;
  /** Avisa as outras abas do silêncio. */
  postMute(until: number): void;
  /** Despede-se (`bye`), solta o lock e fecha o canal. */
  stop(): void;
}

function randomTabId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch {
    // Contexto sem crypto: cai no aleatório simples.
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

interface Peer {
  audioUnlocked: boolean;
  leader: boolean;
  lastSeen: number;
}

/** Escolhe a líder entre as abas vivas: a de som liberado com o menor id; sem nenhuma, a de menor id. */
export function electLeader(tabs: ReadonlyArray<{ id: string; audioUnlocked: boolean }>): string | null {
  if (tabs.length === 0) return null;
  const unlocked = tabs.filter((tab) => tab.audioUnlocked);
  const pool = unlocked.length > 0 ? unlocked : tabs;
  return pool.reduce((min, tab) => (tab.id < min ? tab.id : min), pool[0].id);
}

export function createAlertLeader(options: AlertLeaderOptions): AlertLeader {
  const now = options.now ?? (() => Date.now());
  const tabId = options.tabId ?? randomTabId();
  const pageEvents = options.pageEvents === undefined ? (typeof window === 'undefined' ? null : window) : options.pageEvents;
  const lockName = alertChannelName(options.tenantId);
  const channel = (options.channelFactory ?? browserAlertChannel)(lockName);
  const locks = options.locks === undefined ? browserAlertLocks() : options.locks;
  const peers = new Map<string, Peer>();
  const listeners = new Set<(snapshot: AlertLeaderSnapshot) => void>();
  const muteListeners = new Set<(until: number) => void>();
  let stopped = false;
  let last: AlertLeaderSnapshot | null = null;

  // Estado do lock (só com a Web Locks API).
  let holding = false;
  let stealing = false;
  let release: (() => void) | null = null;
  let waiting: AbortController | null = null;

  const livePeers = (): Array<[string, Peer]> => {
    const at = now();
    return [...peers].filter(([, peer]) => at - peer.lastSeen <= ALERT_PEER_EXPIRY_MS);
  };

  const compute = (): AlertLeaderSnapshot => {
    const ownUnlocked = options.audioUnlocked();
    const alive = livePeers();
    const anyAudioUnlocked = ownUnlocked || alive.some(([, peer]) => peer.audioUnlocked);
    if (locks) return { isLeader: holding, anyAudioUnlocked, coordinated: true };
    if (!channel) return { isLeader: true, anyAudioUnlocked: ownUnlocked, coordinated: false };
    const tabs = [{ id: tabId, audioUnlocked: ownUnlocked }];
    for (const [id, peer] of alive) tabs.push({ id, audioUnlocked: peer.audioUnlocked });
    return { isLeader: electLeader(tabs) === tabId, anyAudioUnlocked, coordinated: true };
  };

  const notify = () => {
    const next = compute();
    if (
      last &&
      last.isLeader === next.isLeader &&
      last.anyAudioUnlocked === next.anyAudioUnlocked &&
      last.coordinated === next.coordinated
    ) {
      return;
    }
    last = next;
    for (const listener of listeners) listener(next);
  };

  const post = (message: AlertChannelMessage) => {
    if (!channel || stopped) return;
    try {
      channel.postMessage(message);
    } catch {
      // Canal fechado pelo navegador: a aba segue sozinha até o próximo ciclo.
    }
  };

  const heartbeat = () =>
    post({ type: 'heartbeat', id: tabId, audioUnlocked: options.audioUnlocked(), leader: compute().isLeader, at: now() });

  /** Pede o lock (ou o toma, com `steal`) e o segura até `stop`, até ser tomado ou até ceder. */
  const acquire = (steal: boolean) => {
    if (!locks || stopped) return;
    waiting?.abort();
    const controller = steal ? null : new AbortController();
    waiting = controller;
    const requestOptions: { mode: 'exclusive'; steal?: boolean; signal?: AbortSignal } = steal
      ? { mode: 'exclusive', steal: true }
      : { mode: 'exclusive', signal: controller!.signal };
    let granted = false;
    let request: Promise<unknown>;
    try {
      request = locks.request(lockName, requestOptions, () => {
        if (stopped) return Promise.resolve();
        granted = true;
        if (waiting === controller) waiting = null;
        holding = true;
        stealing = false;
        notify();
        heartbeat();
        return new Promise<void>((resolve) => {
          release = resolve;
        });
      });
    } catch {
      // Navegador que recusa o lock (contexto inseguro): fica sem liderança por lock.
      stealing = false;
      return;
    }
    request
      .catch(() => {
        // Pedido abortado no `stop`, ou lock tomado por outra aba (`steal`).
      })
      .then(() => {
        if (waiting === controller && !granted) waiting = null;
        if (stealing && steal && !granted) stealing = false;
        if (!granted) return;
        // Soltou (stop ou cessão) ou foi tomado: volta para a fila enquanto viver.
        const resolveHeld = release;
        release = null;
        resolveHeld?.();
        holding = false;
        notify();
        if (!stopped) {
          heartbeat();
          acquire(false);
        }
      });
  };

  /** A aba com som liberado toma a liderança de uma líder sem som. */
  const preferUnlocked = () => {
    if (!locks || stopped || holding || stealing || !options.audioUnlocked()) return;
    const leaderWithoutSound = livePeers().some(([, peer]) => peer.leader && !peer.audioUnlocked);
    if (!leaderWithoutSound) return;
    stealing = true;
    acquire(true);
  };

  const onMessage = (event: MessageEvent<AlertChannelMessage>) => {
    const message = event.data;
    if (!message || typeof message !== 'object') return;
    if (message.type === 'heartbeat') {
      if (message.id === tabId) return;
      const known = peers.has(message.id);
      peers.set(message.id, { audioUnlocked: !!message.audioUnlocked, leader: !!message.leader, lastSeen: now() });
      // A aba nova não espera o próximo ciclo para saber que esta existe.
      if (!known) heartbeat();
      notify();
      preferUnlocked();
    } else if (message.type === 'bye') {
      peers.delete(message.id);
      notify();
    } else if (message.type === 'mute' && typeof message.until === 'number') {
      for (const listener of muteListeners) listener(message.until);
    }
  };

  const prune = () => {
    const at = now();
    for (const [id, peer] of peers) {
      if (at - peer.lastSeen > ALERT_PEER_EXPIRY_MS) peers.delete(id);
    }
  };

  const tick = () => {
    prune();
    heartbeat();
    notify();
  };

  const stop = () => {
    if (stopped) return;
    post({ type: 'bye', id: tabId });
    stopped = true;
    clearInterval(interval);
    pageEvents?.removeEventListener('pagehide', stop);
    waiting?.abort();
    waiting = null;
    const resolveHeld = release;
    release = null;
    resolveHeld?.();
    holding = false;
    if (channel) {
      channel.removeEventListener('message', onMessage);
      try {
        channel.close();
      } catch {
        // Já fechado.
      }
    }
  };

  channel?.addEventListener('message', onMessage);
  pageEvents?.addEventListener('pagehide', stop);
  const interval = setInterval(tick, ALERT_HEARTBEAT_MS);
  heartbeat();
  last = compute();
  acquire(false);

  return {
    tabId,
    snapshot: compute,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    announce: () => {
      heartbeat();
      notify();
      preferUnlocked();
    },
    onMute: (listener) => {
      muteListeners.add(listener);
      return () => muteListeners.delete(listener);
    },
    postMute: (until) => post({ type: 'mute', until }),
    stop,
  };
}
